import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { AudioGenService } from '@app/services/audio-gen.service';
import { ProjectService } from '@app/services/project.service';
import { SseEvent } from '@app/utils/sse-post';
import { extractApiErrorMessage } from '@app/utils/ai-error-prompt';

type AudioGenLineRow = {
  lineSrNo?: number;
  lineId: string;
  agentId?: string;
  agentName?: string;
  scriptText?: string;
  audioFileName?: string;
  status?: string;
  statusMessage?: string;
  hasText?: boolean;
  agentMissing?: boolean;
  textPreview?: string;
};

@Component({
  selector: 'app-audio-gen',
  templateUrl: './audio-gen.component.html',
  styleUrl: './audio-gen.component.scss',
})
export class AudioGenComponent implements OnInit {
  projects: any[] = [];
  customers: any[] = [];
  selectedProjectCode = '';
  creating = false;
  newProject = {
    ProjectShortCode: '',
    ProjectName: '',
    CustomerShortCode: '',
  };

  uploading = false;
  generating = false;
  downloading = false;
  progressPercent = 0;
  progressMessage = '';
  lines: AudioGenLineRow[] = [];
  lastSummary = '';
  selectedFileName = '';

  constructor(
    private audioGenSvc: AudioGenService,
    private projectSvc: ProjectService,
    private toastr: ToastrService,
  ) {}

  customerCode(c: any): string {
    return String(c?.CustomerShortCode ?? c?.customerShortCode ?? '').trim();
  }

  customerName(c: any): string {
    return String(c?.CustomerName ?? c?.customerName ?? '').trim();
  }

  ngOnInit(): void {
    this.reloadProjects();
    this.projectSvc.getCustomers().subscribe({
      next: (res: any) => {
        this.customers = Array.isArray(res) ? res : [];
        if (!this.customers.length) {
          this.toastr.warning('No customers found — add a customer before creating an audio-gen project.');
        }
      },
      error: (err) => {
        this.customers = [];
        this.toastr.error(extractApiErrorMessage(err, 'Failed to load customers'));
      },
    });
  }

  reloadProjects(): void {
    this.audioGenSvc.listProjects().subscribe({
      next: (rows) => {
        this.projects = Array.isArray(rows) ? rows : [];
        if (
          this.selectedProjectCode &&
          !this.projects.some((p) => p.ProjectShortCode === this.selectedProjectCode)
        ) {
          this.selectedProjectCode = '';
        }
      },
      error: (err) => {
        this.projects = [];
        this.toastr.error(extractApiErrorMessage(err, 'Failed to load audio-gen projects'));
      },
    });
  }

  onProjectChange(): void {
    this.lines = [];
    this.lastSummary = '';
    this.progressPercent = 0;
    this.progressMessage = '';
    if (!this.selectedProjectCode) return;
    this.audioGenSvc.getLines(this.selectedProjectCode).subscribe({
      next: (data) => {
        this.lines = Array.isArray(data?.lines) ? data.lines : [];
      },
      error: (err) => {
        this.lines = [];
        this.toastr.error(extractApiErrorMessage(err, 'Failed to load audio-gen lines'));
      },
    });
  }

  createStatus = '';
  createError = '';

  createProject(): void {
    const code = String(this.newProject.ProjectShortCode || '').trim();
    const name = String(this.newProject.ProjectName || '').trim();
    const customer = String(this.newProject.CustomerShortCode || '').trim();
    this.createStatus = '';
    this.createError = '';
    if (!code || !name || !customer) {
      this.createError = 'Short code, name, and customer are required';
      this.toastr.error(this.createError);
      return;
    }
    this.creating = true;
    this.createStatus = `Creating project ${code}…`;
    console.log('[audio-gen] createProject', { code, name, customer });
    this.audioGenSvc
      .createProject({ ProjectShortCode: code, ProjectName: name, CustomerShortCode: customer })
      .subscribe({
        next: (res) => {
          this.creating = false;
          const createdCode = String(res?.ProjectShortCode || code).trim();
          this.createStatus = `Created ${createdCode}`;
          this.createError = '';
          this.toastr.success(`Project ${createdCode} created`);
          this.selectedProjectCode = createdCode;
          this.newProject = { ProjectShortCode: '', ProjectName: '', CustomerShortCode: '' };
          this.reloadProjects();
          this.onProjectChange();
        },
        error: (err) => {
          this.creating = false;
          this.createStatus = '';
          this.createError = extractApiErrorMessage(err, 'Create failed');
          console.error('[audio-gen] createProject failed', err);
          this.toastr.error(this.createError);
          alert(this.createError);
        },
      });
  }

  onFileSelected(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.selectedFileName = file.name;
    if (!this.selectedProjectCode) {
      this.toastr.error('Select or create a project first');
      input.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      this.uploading = true;
      this.audioGenSvc.uploadExcel(this.selectedProjectCode, file.name, dataUrl).subscribe({
        next: (data) => {
          this.uploading = false;
          this.lines = Array.isArray(data?.lines) ? data.lines : [];
          this.toastr.success(data?.message || `Loaded ${data?.lineCount || 0} line(s)`);
          if (data?.truncated) {
            this.toastr.info('Only the first 25 lines were loaded');
          }
        },
        error: (err) => {
          this.uploading = false;
          this.toastr.error(extractApiErrorMessage(err, 'Upload failed'));
        },
      });
    };
    reader.onerror = () => this.toastr.error('Could not read the Excel file');
    reader.readAsDataURL(file);
    input.value = '';
  }

  async generate(): Promise<void> {
    if (!this.selectedProjectCode) {
      this.toastr.error('Select a project first');
      return;
    }
    if (!this.lines.length) {
      this.toastr.error('Upload an Excel file first');
      return;
    }
    if (this.generating) return;
    this.generating = true;
    this.progressPercent = 0;
    this.progressMessage = 'Starting…';
    this.lastSummary = '';
    try {
      await this.audioGenSvc.generateWithProgress(this.selectedProjectCode, (ev: SseEvent) => {
        const type = String(ev?.type || '');
        if (type === 'progress') {
          this.progressPercent = Number(ev['percent'] || 0);
          this.progressMessage = String(ev.message || '');
          const lineId = String(ev['lineId'] || '');
          const status = String(ev['status'] || '');
          if (lineId && status && status !== 'RUNNING') {
            const row = this.lines.find((l) => l.lineId === lineId);
            if (row) {
              row.status = status;
              row.statusMessage = String(ev.message || '');
              if (ev['audioFileName']) row.audioFileName = String(ev['audioFileName']);
            }
          }
        } else if (type === 'done') {
          this.progressPercent = 100;
          this.progressMessage = String(ev.message || 'Done');
          this.lastSummary = this.progressMessage;
          this.toastr.success(this.progressMessage);
        } else if (type === 'error') {
          this.toastr.error(String(ev.message || 'Generation failed'));
          this.progressMessage = String(ev.message || 'Failed');
        }
      });
      this.audioGenSvc.getLines(this.selectedProjectCode).subscribe({
        next: (data) => {
          this.lines = Array.isArray(data?.lines) ? data.lines : this.lines;
        },
      });
    } catch (err: unknown) {
      this.toastr.error(
        extractApiErrorMessage(err, 'Generation failed or timed out before finishing. Try again.'),
      );
      this.progressMessage = extractApiErrorMessage(err, 'Failed');
    } finally {
      this.generating = false;
    }
  }

  downloadZip(): void {
    if (!this.selectedProjectCode) {
      this.toastr.error('Select a project first');
      return;
    }
    this.downloading = true;
    void firstValueFrom(this.audioGenSvc.downloadZip(this.selectedProjectCode))
      .then((blob) => {
        this.downloading = false;
        if (!blob || blob.size === 0) {
          this.toastr.error('ZIP was empty — generate audio first');
          return;
        }
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.selectedProjectCode}_audiogen.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.toastr.success('Audio ZIP downloaded');
      })
      .catch((err) => {
        this.downloading = false;
        this.toastr.error(
          extractApiErrorMessage(err, 'ZIP download failed — generate audio first, or try again if the request timed out.'),
        );
      });
  }

  doneCount(): number {
    return this.lines.filter((l) => String(l.status || '').toUpperCase() === 'DONE').length;
  }
}
